import { createContext, useContext, useState, useEffect } from 'react';

const dark = {
  name: 'dark',
  bg: '#0C0C0E',
  bgSecondary: 'rgba(255,255,255,0.02)',
  bgTertiary: 'rgba(255,255,255,0.03)',
  bgHover: 'rgba(255,255,255,0.04)',
  bgElevated: '#1A1A1E',
  bgInput: 'rgba(255,255,255,0.05)',
  text: '#E8E6E1',
  textSecondary: 'rgba(232,230,225,0.7)',
  textTertiary: 'rgba(232,230,225,0.5)',
  textMuted: 'rgba(232,230,225,0.4)',
  textFaint: 'rgba(232,230,225,0.3)',
  textGhost: 'rgba(232,230,225,0.25)',
  textDisabled: 'rgba(232,230,225,0.2)',
  border: 'rgba(255,255,255,0.06)',
  borderLight: 'rgba(255,255,255,0.04)',
  borderMedium: 'rgba(255,255,255,0.08)',
  borderHover: 'rgba(255,255,255,0.1)',
  accent: '#4F8EF7',
  accentBg: 'rgba(79,142,247,0.1)',
  accentBorder: 'rgba(79,142,247,0.2)',
  accentBgHover: 'rgba(79,142,247,0.15)',
  accentBorderHover: 'rgba(79,142,247,0.3)',
  purple: '#7C5CFC',
  purpleBg: 'rgba(124,92,252,0.12)',
  green: '#45B26B',
  greenBg: 'rgba(69,178,107,0.12)',
  greenBorder: 'rgba(69,178,107,0.3)',
  red: '#E85555',
  redBg: 'rgba(232,85,85,0.1)',
  redBorder: 'rgba(232,85,85,0.2)',
  orange: '#F2994A',
  shadow: '0 8px 24px rgba(0,0,0,0.5)',
  scrollbarThumb: 'rgba(255,255,255,0.08)',
  scrollbarHover: 'rgba(255,255,255,0.15)',
  selectBg: '#1a1a1e',
  cardBg: 'rgba(255,255,255,0.02)',
  cardBorder: 'rgba(255,255,255,0.05)',
  cardBgHover: 'rgba(255,255,255,0.04)',
  cardBorderHover: 'rgba(255,255,255,0.1)',
  headerBg: 'rgba(12,12,14,0.95)',
  badgeBg: 'rgba(255,255,255,0.05)',
  badgeText: 'rgba(232,230,225,0.35)',
  overlay: 'rgba(0,0,0,0.6)',
};

const light = {
  name: 'light',
  bg: '#F5F5F7',
  bgSecondary: 'rgba(0,0,0,0.02)',
  bgTertiary: 'rgba(0,0,0,0.03)',
  bgHover: 'rgba(0,0,0,0.04)',
  bgElevated: '#FFFFFF',
  bgInput: 'rgba(0,0,0,0.04)',
  text: '#1A1A1E',
  textSecondary: 'rgba(26,26,30,0.75)',
  textTertiary: 'rgba(26,26,30,0.55)',
  textMuted: 'rgba(26,26,30,0.45)',
  textFaint: 'rgba(26,26,30,0.35)',
  textGhost: 'rgba(26,26,30,0.25)',
  textDisabled: 'rgba(26,26,30,0.2)',
  border: 'rgba(0,0,0,0.08)',
  borderLight: 'rgba(0,0,0,0.05)',
  borderMedium: 'rgba(0,0,0,0.1)',
  borderHover: 'rgba(0,0,0,0.15)',
  accent: '#3A7BE0',
  accentBg: 'rgba(58,123,224,0.08)',
  accentBorder: 'rgba(58,123,224,0.2)',
  accentBgHover: 'rgba(58,123,224,0.12)',
  accentBorderHover: 'rgba(58,123,224,0.3)',
  purple: '#6B4FCF',
  purpleBg: 'rgba(107,79,207,0.08)',
  green: '#2D9950',
  greenBg: 'rgba(45,153,80,0.08)',
  greenBorder: 'rgba(45,153,80,0.25)',
  red: '#D64545',
  redBg: 'rgba(214,69,69,0.08)',
  redBorder: 'rgba(214,69,69,0.2)',
  orange: '#D97B2A',
  shadow: '0 8px 24px rgba(0,0,0,0.1)',
  scrollbarThumb: 'rgba(0,0,0,0.12)',
  scrollbarHover: 'rgba(0,0,0,0.2)',
  selectBg: '#FFFFFF',
  cardBg: '#FFFFFF',
  cardBorder: 'rgba(0,0,0,0.08)',
  cardBgHover: 'rgba(0,0,0,0.02)',
  cardBorderHover: 'rgba(0,0,0,0.12)',
  headerBg: 'rgba(245,245,247,0.95)',
  badgeBg: 'rgba(0,0,0,0.06)',
  badgeText: 'rgba(26,26,30,0.45)',
  overlay: 'rgba(0,0,0,0.3)',
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('fontvault_theme');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    localStorage.setItem('fontvault_theme', isDark ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const theme = isDark ? dark : light;
  const toggle = () => setIsDark(p => !p);

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
